import React, { useRef, useState } from 'react';
import {
  Box, Grid, Typography, IconButton, Chip, Divider,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useFieldArray, Controller } from 'react-hook-form';
import { toast } from 'react-toastify';
import { InvTextField, InvSelect } from '../InvField';
import InvButton from '../InvButton';
import { IPD_COLORS } from '../../theme';
import {
  emptyDocumentRow,
  readFileAsBase64,
  validateVendorFile,
  documentTypeMeta,
  VENDOR_UPLOAD_DOC_TYPES,
  PHARMA_VENDOR_TYPES,
} from '../../utils/vendorEntity';

export default function VendorDocumentsPanel({ control, watch, setValue, vendorType, disabled = false }) {
  const fileRefs = useRef({});
  const [uploadingIndex, setUploadingIndex] = useState(null);
  const { fields, append, remove } = useFieldArray({ control, name: 'documentRows' });
  const rows = watch('documentRows') || [];

  const usedTypes = new Set(rows.map(r => r?.type).filter(Boolean));
  const isPharma = PHARMA_VENDOR_TYPES.has(vendorType);
  const canAddMore = usedTypes.size < VENDOR_UPLOAD_DOC_TYPES.length;

  function typeOptionsForRow(currentType) {
    return VENDOR_UPLOAD_DOC_TYPES
      .filter(t => t.key === currentType || !usedTypes.has(t.key))
      .map(t => ({ value: t.key, label: t.label }));
  }

  function addRow(presetType = '') {
    if (presetType && usedTypes.has(presetType)) return;
    if (!presetType && !canAddMore) return;
    append(emptyDocumentRow(presetType));
  }

  async function onFilePick(index, file) {
    const err = validateVendorFile(file);
    if (err) {
      toast.error(err);
      return;
    }
    setUploadingIndex(index);
    try {
      const content_base64 = await readFileAsBase64(file);
      const row = rows[index] || {};
      setValue(`documentRows.${index}`, {
        ...row,
        file_name: file.name,
        mime_type: file.type,
        content_base64,
        _fromApi: false,
      }, { shouldDirty: true });
    } catch {
      toast.error('Could not read file. Please try again.');
    } finally {
      setUploadingIndex(null);
    }
  }

  return (
    <Box>
      <Box sx={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        flexWrap: 'wrap', gap: 1, mb: fields.length > 0 ? 2 : 1,
      }}>
        <Typography sx={{ fontSize: 13, color: IPD_COLORS.textSecondary }}>
          Optional — PDF or image, max 5 MB each.
        </Typography>
        {canAddMore && !disabled && (
          <InvButton size="small" startIcon={<AddIcon />} onClick={() => addRow()}>
            Add Document
          </InvButton>
        )}
      </Box>

      {isPharma && !rows.some(r => r?.type === 'DRUG_LICENSE_COPY') && canAddMore && !disabled && (
        <Box sx={{ mb: 2 }}>
          <Chip
            label="+ Drug License Copy (recommended)"
            size="small"
            onClick={() => addRow('DRUG_LICENSE_COPY')}
            sx={{
              cursor: 'pointer',
              backgroundColor: IPD_COLORS.selectedBg,
              color: IPD_COLORS.primary,
              fontWeight: 600,
            }}
          />
        </Box>
      )}

      {fields.length === 0 ? (
        <Typography sx={{ fontSize: 13, color: IPD_COLORS.textSecondary, py: 1 }}>
          No documents added yet.
        </Typography>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          {fields.map((field, index) => {
            const row = rows[index] || {};
            const meta = documentTypeMeta(row.type);
            const hasFile = Boolean(row.file_name);
            const options = typeOptionsForRow(row.type);

            return (
              <Box key={field.id}>
                {index > 0 && <Divider sx={{ my: 2, borderColor: IPD_COLORS.border }} />}
                <Grid container spacing={1.5} alignItems="flex-end">
                  <Grid item xs={12} sm={4}>
                    <Controller
                      name={`documentRows.${index}.type`}
                      control={control}
                      rules={{ required: 'Select document type' }}
                      render={({ field: f, fieldState }) => (
                        <InvSelect
                          label="Document Type *"
                          value={f.value}
                          onChange={f.onChange}
                          options={options}
                          error={fieldState.error}
                          disabled={disabled}
                        />
                      )}
                    />
                  </Grid>

                  {meta?.showNumber && (
                    <Grid item xs={12} sm={3}>
                      <Controller
                        name={`documentRows.${index}.document_number`}
                        control={control}
                        render={({ field: f }) => (
                          <InvTextField {...f} label={meta.numberLabel || 'Document Number'} disabled={disabled} />
                        )}
                      />
                    </Grid>
                  )}

                  <Grid item xs={12} sm={meta?.showNumber ? 5 : 8}>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                      <input
                        ref={(el) => { fileRefs.current[index] = el; }}
                        type="file"
                        accept=".pdf,.jpg,.jpeg,.png"
                        style={{ display: 'none' }}
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) onFilePick(index, file);
                        }}
                      />
                      <InvButton
                        size="small"
                        variant="outlined"
                        tone="teal"
                        startIcon={<UploadFileIcon />}
                        loading={uploadingIndex === index}
                        disabled={disabled}
                        onClick={() => fileRefs.current[index]?.click()}
                      >
                        {hasFile ? 'Replace' : 'Choose File'}
                      </InvButton>
                      {hasFile && (
                        <Chip
                          label={row.file_name}
                          size="small"
                          sx={{
                            maxWidth: 220,
                            backgroundColor: IPD_COLORS.selectedBg,
                            color: IPD_COLORS.primary,
                            fontWeight: 600,
                            fontSize: 11,
                          }}
                        />
                      )}
                      <IconButton
                        size="small"
                        aria-label="Remove document"
                        onClick={() => remove(index)}
                        disabled={disabled}
                        sx={{ color: IPD_COLORS.error.text, ml: 'auto' }}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
}
